import React, { useMemo } from "react";
import { Text } from "@semcore/typography";
import Divider from "@semcore/ui/divider";

import Input from "@semcore/ui/input";
import { Box, Flex } from "@semcore/ui/flex-box";
import Accordion from "@semcore/accordion";
import Link from "@semcore/link";
import Notice from "@semcore/notice";
import Radio, { RadioGroup } from "@semcore/ui/radio";

import Tooltip from "../../tooltip";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

const WebsiteType = ({ website, setWebsite, canTrackURL }) => {
  const email = useSelector((state) => state.email);
  const pathname_loc = useLocation();
  const addonURLPayLink = `https://buy.stripe.com/00g4iR1oG6N46eQ155?prefilled_email=${email}`;
  const domainType = {
    domain: "as Root-domain",
    sub_domain: "as Sub-domain",
    URL: "as Exact-URL",
  };

  const handleChange = (value) => {
    setWebsite((pre) => ({ ...pre, sel_type: value }));
  };
  const formattedDomainDisplay = useMemo(() => {
    if (website.sel_type === "sub_domain") {
      return `${website.sub_domain}.${website.domain}`;
    } else if (website.sel_type === "sub_domain") {
      return `${website.domain}`;
    }

    return website.sel_type === "URL"
      ? website.url || website.domain
      : website.domain;
  }, [website]);

  return (
    <Accordion use="primary">
      <Accordion.Item key="domain-type">
        <Accordion.Item.Toggle pb={2}>
          <Accordion.Item.ToggleButton w={"100%"}>
            <Flex direction="column">
              <Text
                tag="label"
                size={300}
                fontWeight={500}
                htmlFor="domain-type"
              >
                {formattedDomainDisplay || "domain"}
              </Text>
              <Text tag="label" size={200} htmlFor="domain-type">
                {domainType[website.sel_type]}
              </Text>
            </Flex>
            <Accordion.Item.Chevron ml="auto" />
          </Accordion.Item.ToggleButton>
        </Accordion.Item.Toggle>
        <Accordion.Item.Collapse style={{ backgroundColor: "#f4f5f9" }}>
          <Divider orientation="horizontal" theme="default" use="primary" />
          <Box p="10px">
            <RadioGroup
              aria-labelledby="radioGroup"
              name="domain-type"
              value={website.sel_type}
              onChange={handleChange}
            >
              <Flex mt={2} direction={"column"}>
                <Flex alignItems="center" mb={3}>
                  <Radio value={"domain"} label={"as Root domain"} />
                  <Tooltip title="Select this option to track the entire root domain" />
                </Flex>
                <Box>
                  <Flex alignItems="center" mb={2}>
                    <Radio value={"sub_domain"} label={"as Sub-domain"} />
                    <Tooltip
                      title="Select this option to track a subdomain.
Examples: www.apple.com"
                    />
                  </Flex>
                  {website.sel_type === "sub_domain" && (
                    <Input mb={3} size="l" className="sem-input">
                      <Input.Value
                        placeholder="Enter subdomain"
                        aria-labelledby="label-postfix"
                        id="permanent-placeholder"
                        value={website.sub_domain}
                        onChange={(v) =>
                          setWebsite((pre) => ({
                            ...pre,
                            sub_domain: v,
                          }))
                        }
                      />
                      <Input.Addon pr={1}>
                        <Divider
                          orientation="vertical"
                          theme="default"
                          use="primary"
                          mr={1}
                        />
                        <Text color="text-secondary" id="postfix">
                          .{website.domain}
                        </Text>
                      </Input.Addon>
                    </Input>
                  )}
                </Box>
                <Box>
                  <Flex alignItems="center" mb={2}>
                    <Radio
                      value={"URL"}
                      label={"as Exact URL"}
                      disabled={!canTrackURL}
                    />
                    <Tooltip
                      title={
                        <>
                          <em>
                            URL — Tracks an exact URL.
                            <b> Example: www.apple.com/ipodnano/design.html</b>.
                            Please note that URLs with a trailing slash and
                            those without it are completely different URLs
                            altogether. The positions of such URLs may also
                            differ in search engine SERPs.
                            <b> Example: http://www.apple.com/ipod-nano/</b> and
                            <b> http://www.apple.com/ipod-nano</b> are different
                            URLs.
                          </em>
                        </>
                      }
                    />
                    {!canTrackURL && (
                      <Link ml={2} href={addonURLPayLink} target="_blank">
                        Buy Addon
                      </Link>
                    )}
                  </Flex>
                  {website.sel_type === "URL" && (
                    <Input mb={3} size="l" className="sem-input">
                      <Input.Value
                        placeholder="Enter URL"
                        aria-labelledby="label-postfix"
                        id="permanent-placeholder"
                        value={website.url}
                        onChange={(e) => {
                          setWebsite((pre) => ({
                            ...pre,
                            url: e,
                          }));
                        }}
                      />
                    </Input>
                  )}
                </Box>
              </Flex>
            </RadioGroup>
            <Notice hidden={false} theme="info" className="p-2">
              <Notice.Content>
                <b>Note: </b>
                If want to track subdomains, then add the subdomain address
                (blog.example.com), otherwise continue with track only main
                domain (example.com)
              </Notice.Content>
            </Notice>
          </Box>
        </Accordion.Item.Collapse>
      </Accordion.Item>
    </Accordion>
  );
};

export default WebsiteType;
